import useCalculator from './hooks/useCalculator.ts'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import CalculatorForm from './CalculatorForm.tsx'
import CloseIcon from '@mui/icons-material/Close'
import Modal from '@/core/components/modal/Modal.tsx'
import FinancialSummary from '@/core/components/FinancialSummary/FinancialSummary.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { calculatorFormSchema } from '@/modules/utilities/calculator/types/types.ts'
import useCalculatorForm from '@/modules/utilities/calculator/components/calculator/hooks/useCalculatorForm.ts'
import useFinancialInfo from '@/core/hooks/useFinancialInfo/useFinancialInfo.ts'
import dayjs from 'dayjs'
import { useMemo } from 'react'

const CalculatorModal = () => {
  const form = useForm({
    resolver: zodResolver(calculatorFormSchema)
  })

  const { isOpen, closeModal } = useCalculator(form.reset)

  const {
    brands,
    models,
    articles,
    categories,
    rentability,
    handleBuscar,
    isLoadingDebt,
    searchModels,
    isLoadingProm,
    promotionByCedule,
    articlePromotion,
    isLoadingCatRen,
    rentabilityBycategory,
    articleOthers,
    creditDebt,
    articleApple
  } = useCalculatorForm(form, isOpen)

  const credit = form.watch('credit')
  const rentabilityId = form.watch('rentability')
  const cost = form.watch('cost')
  const payment_extra = form.watch('payment_extra')
  const modelId = form.watch('model')

  const discountedCredit = useMemo(() => {
    if (credit && cost) {
      const result = Number(credit) - Number(cost)
      return result < 0 ? 0 : result
    }
  }, [credit, cost])

  const rentabilityValue = useMemo(() => {
    return rentability.options.find((r) => r.id?.toString() === rentabilityId)
      ?.original
  }, [rentability.options, rentabilityId])

  const maxCost = useMemo(() => {
    return models.options.find((m) => m.id?.toString() === modelId)?.maxCost
  }, [models.options, modelId])

  const date = useMemo(() => dayjs(), [])

  // Apple articles use the client's applePercent instead of the level's
  // initial or any promotion; without it, the usual rules apply.
  const applePercent =
    articleApple && creditDebt?.applePercent != null
      ? Number(creditDebt.applePercent)
      : null

  const { financialInformation } = useFinancialInfo({
    initialByLevel: Number(creditDebt?.initialByLevel) || 0,
    clientCreditValue: Number(credit) || 0,
    offerDiscountPercent:
      applePercent !== null
        ? applePercent
        : articlePromotion
          ? Number(articlePromotion.initialPercentage)
          : null,
    offerWithoutCreditLimit:
      applePercent !== null ? false : articlePromotion?.noCreditLimits || false,
    rentabilityTasa:
      Number(articlePromotion?.rentability?.exchange) ||
      Number(rentabilityValue?.exchange) ||
      0,
    rentabilityCantCuotes:
      Number(articlePromotion?.rentability?.quotes) ||
      Number(rentabilityValue?.quotes) ||
      0,
    rentabilityTimesCuotes:
      Number(articlePromotion?.rentability?.timesQuotes) ||
      Number(rentabilityValue?.timesQuotes) ||
      0,
    cost: Number(cost) || 0,
    payment_extra: Number(payment_extra) || 0,
    maxCost: Number(maxCost) || 0,
    date
  })

  const titleNameUser = useMemo(() => {
    return creditDebt
      ? `- ${creditDebt?.clientName} ${creditDebt?.clientLasName}`
      : ''
  }, [creditDebt])

  const financial = useMemo(
    () => (
      <FinancialSummary
        additional_cost={financialInformation.additional_cost}
        initial={financialInformation.initial}
        initial_total={financialInformation.initial_total}
        payment_cuotes={financialInformation.payment_cuotes}
        payment_extra={financialInformation.payment_extra}
        rentability={financialInformation.rentability}
        tasa={financialInformation.tasa}
        total_payment={financialInformation.total_payment}
        list_cuotes={financialInformation.list_cuotes}
        descountPercent={
          applePercent !== null
            ? undefined
            : articlePromotion?.initialPercentage
        }
        discountedCredit={discountedCredit}
        p={2}
      />
    ),
    [financialInformation]
  )

  return (
    <Modal
      onClose={closeModal}
      open={isOpen}
      aria-labelledby="modal-modal-title"
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          maxWidth: '1000px'
        }
      }}
    >
      <Box>
        <Box
          sx={{
            paddingLeft: 2,
            borderBottom: '1px solid #ccc'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ justifyContent: 'space-between', width: '100%' }}
          >
            <Typography id="modal-modal-title" variant="subtitle1">
              <strong>Calculadora</strong> {titleNameUser}
            </Typography>
            <IconButton aria-label="delete" onClick={closeModal}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <Box
          sx={{
            display: 'flex'
          }}
        >
          <Box sx={{ flex: 1, padding: 2 }}>
            <CalculatorForm
              form={form}
              brands={brands}
              models={models}
              articles={articles}
              categories={categories}
              rentability={rentability}
              handleBuscar={handleBuscar}
              isLoadingDebt={isLoadingDebt}
              searchModels={searchModels}
              isLoadingProm={isLoadingProm}
              promotionByCedule={promotionByCedule}
              articlePromotion={articlePromotion}
              rentabilityBycategory={rentabilityBycategory}
              articleOthers={articleOthers}
              articleApple={articleApple}
              isLoadingCatRen={isLoadingCatRen}
            />
          </Box>
          <Box sx={{ width: 300, borderLeft: '1px solid #ccc' }}>
            {financial}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default CalculatorModal
